import { JobAttachmentResponseDto } from 'app/API';
import { resolveNoticeExtractionPollOutcome, TNoticeExtractionPollFailure } from './resolveNoticeExtractionPollOutcome';

export type TNoticeExtractionPollState = {
  consecutiveFailures: number;
  startedAtMs: number;
  inFlight: boolean;
};

export type TNoticeExtractionPollSnapshot = {
  attachment?: JobAttachmentResponseDto;
  workflowState?: string;
};

export type TNoticeExtractionPollTickDeps = {
  fileId: string;
  pollStates: Map<string, TNoticeExtractionPollState>;
  nowMs: () => number;
  fetchSnapshot: () => Promise<TNoticeExtractionPollSnapshot>;
  onAttachmentRefreshed: (attachment: JobAttachmentResponseDto) => void;
  onContinueRunning: () => void;
  onCompleted: () => void;
  onFailed: (failure: TNoticeExtractionPollFailure, status: string) => void;
  stopPoll: () => void;
};

export const runNoticeExtractionPollTick = async (deps: TNoticeExtractionPollTickDeps): Promise<void> => {
  const pollState = deps.pollStates.get(deps.fileId);
  if (!pollState || pollState.inFlight) {
    return;
  }

  pollState.inFlight = true;
  let snapshot: TNoticeExtractionPollSnapshot = {};
  try {
    snapshot = await deps.fetchSnapshot();
    pollState.consecutiveFailures = 0;
  } catch {
    pollState.consecutiveFailures += 1;
  } finally {
    pollState.inFlight = false;
  }

  if (deps.pollStates.get(deps.fileId) !== pollState) {
    return;
  }

  if (snapshot.attachment) {
    deps.onAttachmentRefreshed(snapshot.attachment);
  }

  const outcome = resolveNoticeExtractionPollOutcome({
    attachmentExtracted: snapshot.attachment?.extracted,
    workflowState: snapshot.workflowState,
    consecutiveFailures: pollState.consecutiveFailures,
    elapsedMs: deps.nowMs() - pollState.startedAtMs,
  });

  if (outcome.kind === 'continue') {
    if (snapshot.attachment && !snapshot.attachment.extracted) {
      deps.onContinueRunning();
    }
    return;
  }

  deps.stopPoll();

  if (outcome.kind === 'completed') {
    deps.onCompleted();
    return;
  }

  deps.onFailed(outcome.failure, outcome.status);
};
